import React, { useEffect, useState } from 'react';
import Link from '../common/Link';
import LineChart from '../common/LineChart';
import { ReactComponent as ArrowLeft } from 'assets/img/arrow-left.svg';
import ChartLegend from './ChartLegend';

export default function TractSidePanel({
  children,
  socioeconomicBroadbandData = null,
  socioeconomicBroadbandPredictionsData = null,
  socioeconomicYearsData = null,
  features = null,
  socioeconomicFactor = null,
  backLinkAction,
  activeTract,
}) {
  const [dataChart1, setDataChart1] = useState(null);
  const [dataChart2, setDataChart2] = useState(null);
  const [chart1LegendItems, setChart1LegendItems] = useState([]);
  const [previousActiveTract, setPreviousActiveTract] = useState(null);
  const [previousSocioeconomicFactor, setPreviousSocioeconomicFactor] = useState(null);
  const [previousActiveTractChart2, setPreviousActiveTractChart2] = useState(null);
  const [previousSocioeconomicFactorChart2, setPreviousSocioeconomicFactorChart2] =
    useState(null);

  let socioeconomicFactorLabel = findFeatureLabel(features, socioeconomicFactor);

  useEffect(() => {
    if (
      !activeTract ||
      !socioeconomicBroadbandData ||
      !socioeconomicBroadbandPredictionsData ||
      !socioeconomicFactor
    ) {
      return;
    }

    if (socioeconomicFactor in socioeconomicBroadbandData?.[0] === false) {
      return;
    }

    if (
      activeTract === previousActiveTract &&
      socioeconomicFactor === previousSocioeconomicFactor
    ) {
      return;
    }

    let socioeconomicBroadbandChartLabels = [1, 2, 3, 4, 5];
    let socioeconomicBroadbandChartData1 = socioeconomicBroadbandChartLabels.map(
      (index) =>
        socioeconomicBroadbandData.find((item) => item.broadband === index)?.[
          socioeconomicFactor
        ]
    );

    let socioeconomicBroadbandChartData2 = socioeconomicBroadbandChartLabels.map(
      (index) => {
        return socioeconomicBroadbandPredictionsData.find(
          (item) => item.broadband === index
        )?.[socioeconomicFactor];
      }
    );

    const socioeconomicBroadbandChartDataMerged = socioeconomicBroadbandChartData1.map(
      (value, index) => (isNaN(value) ? socioeconomicBroadbandChartData2[index] : value)
    );

    // Helper function to determine if a data point is standalone
    const isStandaloneDataPoint = (arr, index) => {
      if (index === 0) {
        return isNaN(arr[1]) && !isNaN(arr[0]);
      } else if (index === arr.length - 1) {
        return isNaN(arr[arr.length - 2]) && !isNaN(arr[arr.length - 1]);
      } else {
        return isNaN(arr[index - 1]) && isNaN(arr[index + 1]) && !isNaN(arr[index]);
      }
    };

    // Helper function to determine if a data point is skipped
    const skipped = (ctx, value) =>
      ctx.p0.skip ||
      ctx.p1.skip ||
      isNaN(socioeconomicBroadbandChartData1[ctx.p1DataIndex])
        ? value
        : undefined;

    setDataChart1({
      labels: socioeconomicBroadbandChartLabels,
      datasets: [
        {
          data: socioeconomicBroadbandChartDataMerged,
          borderColor: '#142C60',
          fill: false,
          tension: 0.5,
          spanGaps: false,
          elements: {
            point: {
              backgroundColor: (ctx) => {
                return isStandaloneDataPoint(
                  socioeconomicBroadbandChartData1,
                  ctx.dataIndex
                )
                  ? '#06315D'
                  : 'transparent';
              },
              hoverBackgroundColor: (ctx) => {
                if (isNaN(socioeconomicBroadbandChartData1[ctx.dataIndex]) === false) {
                  return '#06315D';
                }
                return isStandaloneDataPoint(
                  socioeconomicBroadbandChartData1,
                  ctx.dataIndex
                )
                  ? '#06315D'
                  : 'transparent';
              },
              radius: (ctx) => {
                return isStandaloneDataPoint(
                  socioeconomicBroadbandChartData1,
                  ctx.dataIndex
                )
                  ? 3
                  : 5;
              },
            },
          },
          segment: {
            borderColor: (ctx) => skipped(ctx, '#6A839E99'),
            borderDash: (ctx) => skipped(ctx, [6, 6]),
            borderWidth: (ctx) => skipped(ctx, 2),
          },
        },
      ],
    });

    // Filter out predictions for which actual data is already available.
    const predictionsFiltered = socioeconomicBroadbandChartData2.filter((_, i) =>
      isNaN(socioeconomicBroadbandChartData1[i])
    );

    // Set chart 1 legend items.
    const chart1LegendItems = [];

    if (isArrayEmpty(socioeconomicBroadbandChartData1) === false) {
      chart1LegendItems.push({ value: 'blue', label: 'Observation' });
    }
    if (isArrayEmpty(predictionsFiltered) === false) {
      chart1LegendItems.push({ value: 'dashed', label: 'Prediction' });
    }

    setChart1LegendItems(chart1LegendItems);
    setPreviousActiveTract(activeTract);
    setPreviousSocioeconomicFactor(socioeconomicFactor);
  }, [
    activeTract,
    previousActiveTract,
    previousSocioeconomicFactor,
    socioeconomicBroadbandData,
    socioeconomicBroadbandPredictionsData,
    socioeconomicFactor,
  ]);

  useEffect(() => {
    if (!socioeconomicYearsData || !activeTract || !socioeconomicFactor) {
      return;
    }

    if (
      socioeconomicFactor === previousSocioeconomicFactorChart2 &&
      activeTract === previousActiveTractChart2
    ) {
      return;
    }

    if (socioeconomicFactor in socioeconomicYearsData?.[0] === false) {
      return;
    }

    let socioeconomicYearsChartData = socioeconomicYearsData.map(
      (item) => item[socioeconomicFactor]
    );
    let socioeconomicYearsChartLabels = socioeconomicYearsData.map((item) => item.year);

    setDataChart2({
      labels: socioeconomicYearsChartLabels,
      datasets: [
        {
          data: socioeconomicYearsChartData,
          borderColor: function (context) {
            const chart = context.chart;
            const { ctx, chartArea } = chart;

            if (!chartArea) {
              // This case happens on initial chart load
              return;
            }

            return getGradient(ctx, chartArea);
          },
          fill: false,
          tension: 0.5,
        },
      ],
    });

    setPreviousActiveTractChart2(activeTract);
    setPreviousSocioeconomicFactorChart2(socioeconomicFactor);
  }, [
    activeTract,
    previousActiveTractChart2,
    previousSocioeconomicFactorChart2,
    socioeconomicFactor,
    socioeconomicYearsData,
  ]);

  const getGradient = function (ctx, chartArea) {
    let gradient;

    gradient = ctx.createLinearGradient(chartArea.left, 0, chartArea.right, 0);
    gradient.addColorStop(0, '#F3F5F7');
    gradient.addColorStop(0.1, '#D6DFF4');
    gradient.addColorStop(0.5, '#142C60');
    gradient.addColorStop(1, '#142C60');

    return gradient;
  };

  return (
    <div data-component-name='TractSidePanel' className='h-full'>
      <div className='flex flex-col h-full text-blue-600'>
        {backLinkAction && (
          <Link
            action={backLinkAction}
            icon={<ArrowLeft />}
            isIconLeft={true}
            color='text-blue-500/60'
          >
            <span className='text-base -translate-y-px'>back</span>
          </Link>
        )}

        <h2 className='pb-4 mt-4 text-lg font-bold'>Visualizations</h2>

        <div className='h-full overflow-auto pb-7'>
          <h3 className='mt-3 text-sm font-semibold'>
            Correlation between {socioeconomicFactorLabel} and broadband score
          </h3>
          <div className='mt-3 '>
            <LineChart
              data={dataChart1}
              labelY={<span>{socioeconomicFactorLabel}</span>}
              labelX={<span className='pl-3'>Broadband score</span>}
            >
              <ChartLegend items={chart1LegendItems} />
            </LineChart>
          </div>

          <h3 className='text-sm font-semibold mt-7'>
            {socioeconomicFactorLabel} over the years
          </h3>
          <div className='mt-3 '>
            <LineChart
              data={dataChart2}
              labelY={<span>{socioeconomicFactorLabel}</span>}
              labelX={<span className='pl-3'>Years</span>}
            >
              <ChartLegend items={[{ value: 'blue', label: 'Observation' }]} />
            </LineChart>
          </div>
        </div>
      </div>

      {children}
    </div>
  );
}

const findFeatureLabel = function (features, input) {
  if (features === null) {
    return;
  }

  const categoryMatch =
    features.find((categoryData) => categoryData.category === input) || null;

  if (categoryMatch !== null) {
    return categoryMatch.category;
  }

  const featureMatch = features
    .flatMap((categoryData) => categoryData.features)
    .find((featureData) => featureData.id === input);

  return featureMatch ? featureMatch.label : null;
};

const isArrayEmpty = function (array) {
  // Check if all elements in the array are not a number.
  return array.every((element) => isNaN(element) === true);
};
